import type React from 'react';

import { Alert, Button, Link } from '@change/ds-core-ui';

import { Translate, TranslateHtml } from '@change-corgi/core-react/i18n';

import type { SessionUser } from 'src/shared/session';

import { isLoading } from 'src/app/shared/utils/async';

import { LoginForm } from './components/LoginForm';
import { useLoginModalForm } from './hooks/useLoginModalForm';

type Props = {
	user?: SessionUser;
	email: string;
	onSuccess: (user: SessionUser) => void;
};

export const LoginModalForm: React.FC<Props> = ({ user, email: emailProp, onSuccess }) => {
	const {
		data: { userInfo, email, password, formState, resetPasswordState, resetPasswordEmail },
		actions: { setEmail, setPassword, attemptLogin, sendResetPasswordLink },
	} = useLoginModalForm({ user, email: emailProp, onSuccess });

	return (
		<div>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					void attemptLogin();
				}}
				data-qa="login-form"
			>
				<LoginForm
					userInfo={userInfo}
					formState={formState}
					email={email}
					setEmail={setEmail}
					setPassword={setPassword}
					password={password}
				/>
				{resetPasswordState === 'error' && (
					<Alert variant="error" className="mb-2">
						<TranslateHtml value="corgi.login_or_signup.error.network" />
					</Alert>
				)}
				{resetPasswordState === 'success' && (
					<Alert variant="success" className="mb-2">
						<TranslateHtml
							value="corgi.login_or_signup.email.reset"
							replacements={{ emailAddress: resetPasswordEmail }}
						/>
					</Alert>
				)}
				<div className="mb-4 text-right">
					<Link as="button" type="button" data-qa="forgot-password" onClick={sendResetPasswordLink} size="small">
						<Translate value="fe.components.login_or_join.forgot_password" />
					</Link>
				</div>
				<div className="mt-4">
					<Button
						type="submit"
						variant="primary"
						className="w-full"
						disabled={!!formState && isLoading(formState)}
						data-testid="login-modal-submit-button"
					>
						<Translate value="fe.components.login_or_join.log_in" />
					</Button>
				</div>
			</form>
		</div>
	);
};
